import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useEffect, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { fetchCurrentUser, clearJustRegistered } from './store/authSlice';
import { fetchLevels } from './store/levelSlice';
import { fetchSiteConfig, fetchSiteLanguages } from './store/siteSlice';
import { useGetBonusesQuery } from './store/bonusesApi';
import { standaloneRoutes, publicLayoutRoutes, protectedLayoutRoutes } from './routes/routes';
import Layout from 'components/layout/layout';
import ProtectedRoute from 'components/protectedRoute/protectedRoute';
import { LanguageRouteWrapper } from 'components/languageWrapper/LanguageRouteWrapper';
import Loader, { SuspenseLoader } from 'components/loader/loader';
import Modal from 'components/modal/modal';
import ScrollToTop from 'components/scrollToTop/scrollToTop';
import AssetPreloader from 'components/utils/AssetPreloader';
import { SocketProvider } from 'components/socket/SocketProvider';
import GlobalToast from 'components/toast/globalToast';
import { useIntercom } from 'hooks/useIntercom';
import { useLanguageCacheInvalidation } from 'hooks/useLanguageCacheInvalidation';
import GeoBlockedModal from 'components/geoBlockedModal/geoBlockedModal';
import { socketService } from './services/socketService';

const EmailVerification = React.lazy(
  () => import('components/emailVerification/emailVerification'),
);

const renderLayoutRoutes = () => (
  <Route element={<Layout />}>
    {publicLayoutRoutes.map((route) => {
      const subPath = route.path === '/' ? '' : route.path.replace(/^\//, '');
      return <Route key={route.path} path={subPath} element={route.element} />;
    })}
    <Route element={<ProtectedRoute />}>
      {protectedLayoutRoutes.map((route) => {
        const subPath = route.path.replace(/^\//, '');
        return <Route key={route.path} path={subPath} element={route.element} />;
      })}
    </Route>
  </Route>
);

function AppContent() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user, justRegistered } = useAppSelector((state) => state.auth);
  const { config } = useAppSelector((state) => state.site);

  useEffect(() => {
    const isLogin = localStorage.getItem('isLogin') === 'true';
    if (isLogin) {
      dispatch(fetchCurrentUser());
    }
    dispatch(fetchLevels());
    dispatch(fetchSiteConfig());
    dispatch(fetchSiteLanguages());
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        socketService.forceReconnect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  useIntercom();
  useLanguageCacheInvalidation();

  const { refetch: refetchBonuses } = useGetBonusesQuery(undefined, {
    pollingInterval: isAuthenticated ? 7 * 60 * 1000 : 0,
  });

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchLevels());
      refetchBonuses();
    }
  }, [isAuthenticated, dispatch, refetchBonuses]);

  const isEmailVerificationModalOpen = !!(
    justRegistered &&
    isAuthenticated &&
    user &&
    !user.isEmailVerified
  );

  return (
    <>
      <AssetPreloader />
      <Loader />
      <Suspense fallback={<SuspenseLoader />}>
        <Routes>
          {/* Standalone routes (e.g. Google auth callback) */}
          {standaloneRoutes.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}

          {/* Root clean routes (default language: English, no /en in URL) */}
          <Route path="/" element={<LanguageRouteWrapper isRoot />}>
            {renderLayoutRoutes()}
          </Route>

          {/* Localized routes under /:lang (for non-default languages, e.g. /ru, /tr) */}
          <Route path="/:lang" element={<LanguageRouteWrapper />}>
            {renderLayoutRoutes()}
          </Route>
        </Routes>
      </Suspense>

      {isEmailVerificationModalOpen && (
        <Suspense fallback={<SuspenseLoader />}>
          <Modal
            isOpen={isEmailVerificationModalOpen}
            onClose={() => {
              dispatch(clearJustRegistered());
            }}
            title={t('auth.emailVerification.modalTitle', 'Email Verification')}
            hideCloseButton
          >
            <EmailVerification />
          </Modal>
        </Suspense>
      )}

      {config?.geo?.isBlocked && <GeoBlockedModal />}
      <GlobalToast />
    </>
  );
}

function App() {
  return (
    <Router>
      <SocketProvider>
        <ScrollToTop />
        <AppContent />
      </SocketProvider>
    </Router>
  );
}

export default App;
